/**
 * HelpDrawer content registry — route-based contextual help.
 * Also serves as the data source for the Guide / Tutorials page.
 */

export interface HelpEntry {
  title: string;
  purpose: string;
  howTo: string;
  whenToUse: string;
  tips: string;
  example: string;
  /** Category for guide page grouping */
  category?: GuideCategory;
  /** Icon emoji for guide cards */
  icon?: string;
  /** Sort order within category (lower = first) */
  order?: number;
  /** Minimum interface mode to show this entry in the guide */
  minMode?: "simple" | "core" | "advanced";
  /** Preview text shown at lower modes to tease what higher modes unlock */
  previewText?: string;
}

export type GuideCategory =
  | "getting-started"
  | "sessions"
  | "programs"
  | "planning"
  | "progression"
  | "advanced";

export const GUIDE_CATEGORIES: { key: GuideCategory; label: string; icon: string }[] = [
  { key: "getting-started", label: "Premiers pas", icon: "🚀" },
  { key: "sessions", label: "Séances", icon: "⚡" },
  { key: "programs", label: "Programmes", icon: "📋" },
  { key: "planning", label: "Planning & Calendrier", icon: "📅" },
  { key: "progression", label: "Progression", icon: "📈" },
  { key: "advanced", label: "Modules & outils avancés", icon: "🔧" },
];

export const helpContent: Record<string, HelpEntry> = {
  "/brain-dashboard": {
    title: "Commande 'performe'",
    category: "getting-started",
    icon: "⚡",
    order: 3,
    purpose: "Tape 'performe' dans le chat du Cerveau pour obtenir un résumé instantané : état de ton profil, séance du jour, prochains entraînements et conseil prioritaire.",
    howTo: "Ouvre le Cerveau (bouton flottant ou touche K) → tape 'performe' → Entrée. La réponse arrive en 2-3 secondes.",
    whenToUse: "Chaque matin pour voir ta journée d'un coup d'œil, ou quand tu veux reprendre après une absence.",
    tips: "Si tu n'as pas encore de programme actif, le Cerveau te guidera vers la prochaine étape. Complète d'abord ton profil pour des résumés plus précis.",
    example: "Tu tapes 'performe' → le Cerveau répond : profil à 80%, séance Push prévue aujourd'hui (non faite), prochains : Pull mercredi, Legs vendredi, conseil : tu n'as pas fait de check-in depuis 3 jours.",
  },
  "/onboarding": {
    title: "Onboarding — Ton profil sportif",
    category: "getting-started",
    icon: "🎯",
    order: 1,
    purpose:
      "C'est la première étape indispensable. L'onboarding collecte tes objectifs (force, perte de poids, santé…), ton niveau, tes sports, tes contraintes (blessures, matériel) et tes disponibilités. Toutes ces données servent à personnaliser chaque programme, séance et conseil que tu recevras.",
    howTo:
      "Réponds aux questions étape par étape dans le quiz. Tu peux aussi choisir le mode « discussion guidée » si tu préfères un échange libre avec le cerveau. Tes réponses sont sauvegardées automatiquement à chaque étape — tu peux quitter et revenir sans perdre ta progression. À la fin, un récapitulatif te montre ton profil avant validation.",
    whenToUse:
      "Tu passes par l'onboarding une seule fois, à ta première connexion. Si tu veux modifier tes réponses plus tard, tu peux le faire depuis ta page Profil ou ta Fiche Athlète.",
    tips:
      "Sois honnête sur ton niveau et tes contraintes : plus tes réponses sont précises, meilleurs seront tes programmes. Si tu hésites sur une question, choisis ce qui te correspond le plus — tu pourras toujours ajuster après.",
    example:
      "Tu choisis « Force / Puissance » comme objectif, « Musculation » + « Course à pied » comme sports, 4 jours/semaine, avec une douleur d'épaule ancienne → le système adapte tout : exercices, volume, prévention.",
  },
  "/athlete-profile": {
    title: "Fiche Athlète",
    category: "getting-started",
    icon: "🏅",
    order: 2,
    purpose:
      "Ta fiche athlète est ton identité sportive. Elle affiche ton type d'athlète (Puissance, Endurance, Hybride…), tes forces, tes axes d'amélioration, et des recommandations personnalisées basées sur l'analyse de ton profil.",
    howTo:
      "Consulte ta fiche pour voir ton radar de compétences, ton type d'athlète et les conseils associés. Tu peux mettre à jour ton profil sportif (historique, blessures, sports pratiqués) directement depuis cette page. Chaque modification déclenche une ré-analyse automatique.",
    whenToUse:
      "Consulte ta fiche après l'onboarding pour découvrir ton type, puis régulièrement pour suivre l'évolution de tes recommandations. Reviens ici quand tu ajoutes un sport, une blessure, ou que ton niveau change.",
    tips:
      "Le radar te montre tes points forts et faibles en un coup d'œil. Utilise les recommandations pour orienter tes prochains programmes. Si tu sens que ton profil a changé (nouveau sport, blessure guérie), mets-le à jour ici.",
    example:
      "Type « Puissance » → forces en force/explosivité, point d'attention sur la mobilité → recommandation d'ajouter du stretching dynamique et des séances de mobilité articulaire.",
  },
  "/programs": {
    title: "Mes Programmes",
    category: "programs",
    icon: "💪",
    order: 1,
    purpose:
      "Consulte et gère tes programmes d'entraînement. Chaque programme est personnalisé selon ton profil athlète et contient plusieurs semaines de séances structurées. Les jours indiqués dans le programme sont des suggestions — tu peux librement déplacer les séances dans ton planning sans casser la progression.",
    howTo:
      "La page affiche tes programmes existants et te permet d'en créer de nouveaux via le Cerveau IA ou manuellement. Active un programme pour qu'il apparaisse dans ton planning et ton tableau de bord. Pour changer le jour d'une séance, utilise le bouton ↔ à côté de chaque bloc — le programme reste intact, seul le placement change.",
    whenToUse:
      "Viens ici pour consulter ton programme en cours, en créer un nouveau, ou changer de programme.",
    tips:
      "Active un programme pour qu'il apparaisse dans ton planning et ton tableau de bord. Le scan de compatibilité vérifie automatiquement les conflits. N'hésite pas à réorganiser les jours pour coller à ta vraie semaine — le programme définit la structure, le planning définit le calendrier.",
    example:
      "Tu génères un programme PPL 4 semaines via le Cerveau → il apparaît dans ta liste → tu l'actives → tes séances apparaissent dans le Planning. Tu déplaces « Push » de Lundi à Mardi car tu as déjà un entraînement le lundi → la progression reste identique.",
  },
  "/sessions": {
    title: "Mes Séances",
    category: "sessions",
    icon: "⚡",
    order: 1,
    purpose:
      "Crée, gère et lance tes séances d'entraînement. Une séance est un entraînement précis avec des exercices, séries et temps de repos.",
    howTo:
      "L'onglet « Mes séances » affiche toutes tes séances. L'onglet « Créer » te permet de générer une séance avec l'IA ou de la construire manuellement. Clique sur une séance pour la visualiser, puis modifie-la, duplique-la ou lance-la.",
    whenToUse:
      "Viens ici quand tu veux créer une nouvelle séance, consulter tes séances existantes, ou en lancer une.",
    tips:
      "Tu peux dupliquer une séance existante pour la modifier sans perdre l'originale. Utilise les favoris pour retrouver rapidement tes séances préférées.",
    example:
      "Tu crées une séance « Push jour 1 » avec 5 exercices → tu la retrouves dans ta liste → tu la lances le lundi → tu la dupliques et l'adaptes pour le jeudi.",
  },
  "/planning": {
    title: "Planning hebdomadaire",
    category: "planning",
    icon: "📅",
    order: 1,
    purpose:
      "Organise tes séances dans la semaine. Le programme définit ta structure d'entraînement (ordre, focus, progression), le planning définit quand tu t'entraînes réellement. Tu peux déplacer n'importe quelle séance sur un autre jour sans casser ton programme.",
    howTo:
      "Place tes blocs d'entraînement sur les jours de la semaine. Configure tes types de semaine (normale, intense, récupération) et le planning s'adapte automatiquement. Pour déplacer une séance, utilise le bouton ↔ sur chaque bloc — seul le jour change, le contenu et la progression restent identiques.\n\n📱 Sur mobile, deux modes d'affichage sont disponibles :\n• Vue Semaine — Aperçu global de toute la semaine, idéal pour scanner ton planning rapidement.\n• Vue Jour — Affichage pleine largeur d'un seul jour, mode recommandé pour ajouter ou modifier des créneaux avec précision.\n\nUn contrôle de densité (Compact / Normal / Large) te permet d'ajuster le zoom de la grille selon tes préférences de lecture.",
    whenToUse:
      "Viens ici pour organiser ta semaine, ajuster tes créneaux, ou configurer un cycle d'entraînement. C'est aussi ici que tu adaptes les jours suggérés par le programme à ta vraie semaine (travail, cours, famille…).",
    tips:
      "Les jours du programme sont des suggestions, pas des obligations. Déplace librement tes séances pour coller à ta vie réelle — le système vérifie la cohérence et te prévient en cas de risque (ex : deux séances intenses d'affilée).\n\n📱 Sur mobile : utilise la vue Semaine pour vérifier ta répartition globale, puis passe en vue Jour pour éditer. Le mode Compact te donne le maximum de visibilité, le mode Large facilite la lecture sur petit écran.",
    example:
      "Le programme suggère Lundi/Mercredi/Vendredi → tu déplaces sur Mardi/Jeudi/Samedi car tu as cours le lundi → la progression reste parfaitement identique.",
  },
  "/dashboard": {
    title: "Tableau de bord",
    category: "getting-started",
    icon: "📊",
    order: 3,
    purpose:
      "Ton centre de contrôle quotidien. Le tableau de bord te donne une vue d'ensemble de ta journée : séance prévue, progression, bilan quotidien (check-in), alertes du cerveau central, et actions recommandées.",
    howTo:
      "En arrivant, tu vois ta carte du jour avec la séance prévue et ton statut. Utilise les actions rapides pour lancer une séance, faire ton check-in quotidien, ou consulter ton journal. Le cerveau central (accessible via le dock flottant) t'envoie des alertes et conseils personnalisés.",
    whenToUse:
      "C'est ta page d'accueil après connexion. Consulte-la chaque jour pour voir ce qui est prévu et ce que le cerveau te recommande. C'est aussi ici que tu fais ton check-in quotidien (entraînement fait, nutrition, sommeil, douleurs).",
    tips:
      "Fais ton check-in quotidien même les jours de repos — ça nourrit l'analyse du cerveau. Si tu vois une alerte rouge, lis-la : elle peut signaler un risque de surentraînement ou un déséquilibre.",
    example:
      "Lundi matin → tu vois « Séance Push prévue à 18h » → tu lances la séance → après, tu fais ton check-in : entraînement ✅, sommeil 7h, aucune douleur.",
  },
  "/profile": {
    title: "Profil & Réglages",
    category: "advanced",
    icon: "👤",
    order: 3,
    minMode: "core",
    purpose:
      "Gérer ton identité sur l'app : pseudo, nom d'affichage, niveau sportif, confidentialité. C'est aussi ici que tu vois ton historique de modifications de profil.",
    howTo:
      "Modifie tes infos depuis les différents onglets : informations générales, progression, signalements. Tu peux changer ton pseudo, ton niveau sportif, ou ajuster ta confidentialité (anonyme, pseudo visible, etc.).",
    whenToUse:
      "Viens ici quand tu veux modifier tes préférences d'affichage, changer ton pseudo, ou ajuster ton niveau. Si tu partages du contenu en communauté, vérifie ici ton statut de confidentialité.",
    tips:
      "Ton pseudo est ce qui apparaît en communauté, choisis-le bien.",
    example:
      "Tu changes ton pseudo en « AlexFit », tu passes en confidentialité « pseudo visible » → tes partages en communauté afficheront « AlexFit » au lieu de « Anonyme ».",
  },
  "/library": {
    title: "Bibliothèque scientifique",
    category: "advanced",
    icon: "📚",
    order: 1,
    minMode: "advanced",
    previewText: "En mode Avancé, explore les faits scientifiques qui alimentent tes programmes.",
    purpose:
      "Explorer les faits scientifiques validés qui alimentent tes programmes. Chaque fait est extrait de sources vérifiées (études, livres, experts) et classé par thème, niveau de confiance et difficulté.",
    howTo:
      "Utilise la barre de recherche pour trouver des faits par mot-clé. Filtre par tags (hypertrophie, mobilité, nutrition…), niveau de confiance, ou difficulté. Clique sur un fait pour voir ses sources et citations. Tu peux aussi soumettre de nouvelles sources à l'équipe.",
    whenToUse:
      "Consulte la bibliothèque quand tu veux comprendre pourquoi un exercice est dans ton programme, ou quand tu cherches des informations fiables sur un sujet sportif. C'est aussi utile si tu veux contribuer en soumettant des sources.",
    tips:
      "Les faits avec un score de confiance élevé (≥ 0.8) sont les plus fiables. Les badges de source (étude, livre, expert) t'indiquent l'origine. Si tu trouves une erreur ou une source douteuse, utilise le bouton « Signaler ».",
    example:
      "Tu cherches « hypertrophie volume » → tu trouves un fait « 10-20 séries par groupe musculaire par semaine est optimal pour l'hypertrophie » (confiance 0.85, source : méta-analyse 2023).",
  },
  "/community": {
    title: "Communauté",
    category: "advanced",
    icon: "👥",
    order: 2,
    minMode: "core",
    previewText: "En mode Standard, partage tes programmes et découvre ceux de la communauté.",
    purpose:
      "Partager tes programmes et séances avec d'autres utilisateurs, et découvrir les contenus partagés par la communauté. Tu peux liker, sauvegarder, commenter, et t'inspirer des entraînements des autres.",
    howTo:
      "Parcours le fil de la communauté pour voir les programmes et séances partagés. Like (❤️) pour soutenir, sauvegarde (🔖) pour retrouver plus tard, et commente pour échanger. Pour partager ton propre contenu, va dans tes programmes et utilise le bouton « Partager ».",
    whenToUse:
      "Viens ici quand tu cherches de l'inspiration, que tu veux voir ce que font les autres utilisateurs, ou que tu veux partager un programme dont tu es fier. C'est aussi l'endroit pour donner et recevoir des retours.",
    tips:
      "Les contenus les plus likés et sauvegardés remontent en haut. Tu peux filtrer par type (programme, séance). Quand tu partages, ajoute un commentaire pour expliquer le contexte — ça aide les autres à comprendre ton approche.",
    example:
      "Tu partages ton programme « Force 4 semaines » avec le commentaire « Programme testé pendant 2 mois, +15 kg au squat » → les membres likent et posent des questions.",
  },
  "/nutrition": {
    title: "Nutrition",
    category: "progression",
    icon: "🍎",
    order: 2,
    purpose:
      "Consulter tes recommandations nutritionnelles personnalisées en fonction de ton profil, tes objectifs et ton niveau d'activité. La nutrition est un pilier clé de ta progression.",
    howTo:
      "Consulte les conseils et guides adaptés à ton profil. Les recommandations s'ajustent automatiquement selon ton objectif (prise de masse, sèche, performance…) et ton programme en cours.",
    whenToUse:
      "Consulte cette page quand tu planifies tes repas de la semaine, que tu veux comprendre tes besoins caloriques, ou que tu cherches des conseils spécifiques (pré/post-entraînement, hydratation…).",
    tips:
      "La nutrition est souvent le facteur limitant de la progression. Même un bon programme d'entraînement donne peu de résultats sans une alimentation adaptée. Commence par les bases (protéines suffisantes, hydratation) avant les détails.",
    example:
      "Objectif « Prise de masse » + 4 séances/semaine → recommandation de 1.8g de protéines/kg/jour, répartition glucides/lipides adaptée aux jours d'entraînement vs repos.",
  },
  "/prevention": {
    title: "Prévention & Blessures",
    category: "progression",
    icon: "🩹",
    order: 3,
    purpose:
      "Prévenir les blessures et gérer les douleurs grâce à des exercices de mobilité, stretching, et renforcement ciblés. Cette section est personnalisée selon tes antécédents et tes zones à risque.",
    howTo:
      "Consulte les exercices de prévention recommandés pour tes zones sensibles. Le système analyse ton profil (blessures passées, type d'entraînement, déséquilibres) pour te proposer des routines ciblées.",
    whenToUse:
      "Consulte cette page en début de programme pour intégrer la prévention à ta routine, ou dès que tu ressens une gêne/douleur. C'est aussi utile après une longue pause pour reprendre en sécurité.",
    tips:
      "La prévention est plus efficace quand elle est régulière (5-10 min/jour) plutôt qu'occasionnelle. Signale tes douleurs via le check-in quotidien du tableau de bord — le système ajustera automatiquement ses recommandations.",
    example:
      "Antécédent de douleur d'épaule + programme push/pull → recommandation de routine d'échauffement spécifique épaule (rotateurs, face pulls) avant chaque séance push.",
  },
  "/avatar": {
    title: "Projection Avatar",
    category: "progression",
    icon: "🧬",
    order: 4,
    purpose:
      "Visualiser ta projection physique et sportive à moyen/long terme en fonction de tes objectifs et de ta régularité. L'avatar est une représentation motivante de ce que tu peux atteindre.",
    howTo:
      "Consulte ton avatar de projection qui évolue selon ta progression, tes check-ins et tes objectifs. C'est une visualisation pour te motiver et te donner une perspective sur ton parcours.",
    whenToUse:
      "Viens ici quand tu veux un boost de motivation ou que tu veux voir l'impact de ta régularité sur ta progression projetée.",
    tips:
      "L'avatar se base sur tes données réelles (check-ins, séances complétées, progression). Plus tu es régulier, plus la projection est positive. Ce n'est pas une prédiction exacte, mais un outil de motivation.",
    example:
      "3 mois de régularité (80%+ des séances faites) → ta projection montre une amélioration significative de ta composition corporelle et de tes performances.",
  },

  // === Tutorials (not bound to a route) ===

  "/guide/calendar-sync": {
    title: "Synchroniser ton planning avec ton calendrier",
    category: "planning",
    icon: "📅",
    order: 1,
    purpose:
      "Tu peux exporter tes séances d'entraînement vers ton calendrier personnel (Google Calendar, Apple Calendar, Outlook). Tes créneaux sport apparaissent directement dans ton agenda, avec le titre, la durée et un lien pour ouvrir la séance dans l'app.",
    howTo:
      "1. Va dans l'onglet Planning de la page Programmes & Séances.\n2. Ouvre un programme actif.\n3. Clique sur le bouton « 📅 Ajouter au calendrier ».\n4. Choisis le scope : un jour, la semaine en cours, ou tout le programme.\n5. Si une séance n'a pas d'heure, l'app te demandera d'en choisir une.\n6. L'événement s'ouvre dans ton calendrier.\n\n— Apple Calendar (iPhone / Mac) —\nLe fichier .ics s'ouvre automatiquement dans l'app Calendrier. Accepte l'ajout.\n\n— Google Calendar —\nSur Android, tu es redirigé vers Google Calendar avec les infos pré-remplies. Sur desktop, ouvre Google Calendar → ⚙️ Paramètres → Importer/Exporter → Importer le fichier .ics.\n\n— Outlook —\nOuvre le fichier .ics téléchargé → Outlook propose l'ajout automatique. Ou va dans Fichier → Ouvrir & Exporter → Importer depuis un fichier iCalendar.",
    whenToUse:
      "Utilise cette fonction quand tu veux voir tes séances dans ton agenda du quotidien, pour ne rien oublier et organiser ta semaine autour de ton entraînement.",
    tips:
      "⚠️ Le calendrier exporté est en lecture seule : les modifications de planning doivent être faites dans l'app, pas dans ton calendrier externe.\n⚠️ La synchronisation n'est pas automatique : si tu changes ton planning, ré-exporte les séances concernées.\n⚠️ Un badge « Ajouté ✅ » apparaît sur les séances déjà exportées pour t'aider à ne pas dupliquer.",
    example:
      "Tu exportes ta semaine de Push/Pull/Legs → 3 événements apparaissent dans Google Calendar : Lundi 18h « ⚡ Push – Pecs & Épaules (60 min) », Mercredi 18h « 💪 Pull – Dos & Biceps (55 min) », Vendredi 18h « 🏃 Legs – Squat & Fentes (65 min) ».",
  },
  "/guide/create-session": {
    title: "Créer une séance d'entraînement",
    category: "sessions",
    icon: "⚡",
    order: 1,
    purpose:
      "Apprends à créer une séance personnalisée, que ce soit via l'IA (le Cerveau) ou manuellement. Une séance est un entraînement précis avec des exercices, séries, repos et notes.",
    howTo:
      "1. Va dans Programmes & Séances → onglet « Créer ».\n2. Choisis le mode :\n   • Cerveau — Décris ton objectif en texte libre, l'IA génère la séance.\n   • Manuel — Ajoute tes exercices un par un.\n3. Remplis la durée, le niveau, les tags.\n4. Sauvegarde → la séance apparaît dans l'onglet « Séances ».\n\nPour modifier une séance existante :\n→ Onglet « Séances » → bouton « Modifier » sur la carte.",
    whenToUse:
      "Quand tu veux un entraînement précis pour un objectif donné (force, vitesse, mobilité…) ou quand tu veux construire une séance sur mesure.",
    tips:
      "En mode Cerveau, sois précis dans ta description : « Séance haut du corps, focus épaules, 45 min, pas de développé couché » donne de meilleurs résultats que « Séance sport ». Tes contraintes et blessures sont prises en compte automatiquement.",
    example:
      "Tu tapes « Séance jambes, focus squat et fentes, 50 min, intermédiaire » → le Cerveau génère 6 exercices avec séries, repos et notes adaptés à ton profil.",
  },
  "/guide/manage-sessions": {
    title: "Gérer tes séances",
    category: "sessions",
    icon: "🗂️",
    order: 2,
    purpose:
      "Apprends à retrouver, modifier, dupliquer, archiver et organiser tes séances d'entraînement.",
    howTo:
      "1. Va dans Programmes & Séances → onglet « Séances ».\n2. Utilise la barre de recherche pour trouver une séance par nom, objectif ou tag.\n3. Filtre par statut : Actives, Archivées, Toutes.\n4. Sur chaque carte de séance, tu peux :\n   • Modifier — Changer le titre, les exercices, la durée\n   • Dupliquer — Créer une copie modifiable\n   • Ajouter à un programme — Rattacher à un programme existant\n   • Archiver — Masquer sans supprimer\n   • Supprimer — Suppression définitive",
    whenToUse:
      "Quand tu veux retrouver une ancienne séance, adapter une séance existante pour un nouveau contexte, ou faire le tri dans tes créations.",
    tips:
      "Duplique plutôt que modifier si tu veux garder l'original intact. L'archivage est réversible (restauration possible), la suppression ne l'est pas.",
    example:
      "Tu dupliques ta séance « Force Jambes » → tu la renommes « Force Jambes – léger » → tu réduis le nombre de séries → tu la rattaches à ton programme de décharge.",
  },
  "/guide/create-program": {
    title: "Créer un programme d'entraînement",
    category: "programs",
    icon: "📋",
    order: 2,
    purpose:
      "Un programme est un plan structuré sur plusieurs semaines. Apprends à en créer un via le Cerveau ou manuellement.",
    howTo:
      "1. Va dans Programmes & Séances → onglet « Créer ».\n2. Choisis le mode :\n   • Cerveau — Décris tes objectifs, l'IA génère un programme complet.\n   • Manuel — Construis bloc par bloc, semaine par semaine.\n3. Le programme généré inclut : vue d'ensemble, semaines, séances, et progression.\n4. Active-le pour qu'il apparaisse dans ton Planning.",
    whenToUse:
      "Quand tu veux un cadre d'entraînement sur 4 à 12 semaines avec une progression intégrée.",
    tips:
      "Un seul programme actif à la fois. Tu peux en avoir plusieurs en bibliothèque et alterner selon tes phases (masse, sèche, décharge…).",
    example:
      "Tu demandes « Programme PPL 4 semaines, 4 jours/semaine, focus hypertrophie » → le Cerveau génère 4 semaines avec progression de volume.",
  },
  "/guide/planning": {
    title: "Organiser ton planning hebdomadaire",
    category: "planning",
    icon: "🗓️",
    order: 2,
    purpose:
      "Le planning te permet de voir ta semaine d'entraînement et d'y placer tes séances aux bons créneaux.",
    howTo:
      "1. Va dans Programmes & Séances → onglet « Planning ».\n2. Ta semaine affiche les jours avec les séances planifiées.\n3. Clique « Ajouter » sur un jour pour y placer une séance.\n4. Tu peux déplacer, retirer ou remplacer des séances.\n5. Le Cerveau peut proposer des créneaux intelligents basés sur ton historique.\n\n📱 Sur mobile, utilise la barre d'outils en haut de la grille :\n• Semaine — Vue globale des 7 jours pour scanner ta semaine.\n• Jour — Vue détaillée d'un seul jour pour éditer précisément tes créneaux. Navigue entre les jours avec les flèches ou les pastilles.\n• Densité — Choisis Compact (voir plus de créneaux), Normal (équilibré), ou Large (lecture facile).",
    whenToUse:
      "Chaque début de semaine pour organiser tes entraînements, ou quand ton emploi du temps change.",
    tips:
      "Respecte les jours de repos proposés. Si tu déplaces une séance, le système vérifie automatiquement les conflits et les surcharges.\n\n📱 Astuce mobile : commence en vue Semaine pour avoir une vue d'ensemble, puis passe en vue Jour pour ajouter ou modifier un créneau précis. Le mode Compact est idéal pour la vue Semaine, le mode Large pour la vue Jour.",
    example:
      "Lundi Push, Mercredi Pull, Vendredi Legs → tu déplaces Mercredi à Jeudi car réunion → le système valide qu'il n'y a pas de surcharge.",
  },
  "/guide/checkin": {
    title: "Faire ton check-in quotidien",
    category: "progression",
    icon: "✅",
    order: 1,
    purpose:
      "Le check-in est ton bilan rapide de fin de journée. Il alimente le Cerveau pour personnaliser tes recommandations et détecter les risques.",
    howTo:
      "1. Depuis le Tableau de bord, utilise la section check-in.\n2. Indique : entraînement fait ✅/❌, sommeil (heures), nutrition ✅/❌, douleurs éventuelles.\n3. Le Cerveau analyse tes données et ajuste ses alertes.",
    whenToUse:
      "Chaque jour, même les jours de repos. Plus tu es régulier, plus les recommandations sont précises.",
    tips:
      "Le check-in prend 30 secondes. C'est la donnée la plus précieuse pour le suivi de ta progression et la prévention du surentraînement.",
    example:
      "Lundi soir → entraînement fait ✅, sommeil 6h ⚠️, légère douleur genou → le Cerveau te propose d'adapter la séance de demain et de dormir plus.",
  },
  "/settings": {
    title: "Clés API — Accès programmatique",
    category: "advanced",
    icon: "🔑",
    order: 10,
    minMode: "advanced",
    previewText: "Génère des clés API pour connecter tes agents IA et scripts à PERFORM.",
    purpose:
      "Les clés API te permettent d'accéder à PERFORM depuis tes agents IA (Claude, ChatGPT, scripts Python…) sans passer par l'interface. Tu peux interroger le Cerveau, lire ton profil, générer des séances et déclencher des fonctions de manière automatisée.",
    howTo:
      "1. Va dans Profil → onglet « API & Intégrations » (ou ouvre Paramètres).\n2. Clique « Nouvelle clé », donne-lui un nom (ex: claude-agent).\n3. La clé s'affiche une seule fois — copie-la immédiatement.\n4. Dans ton script ou agent, ajoute le header :\n   Authorization: Bearer pk_perform_XXXXXX\n5. Endpoint de base : https://[projet].supabase.co/functions/v1/perform-api",
    whenToUse:
      "Quand tu veux automatiser des actions PERFORM depuis un agent IA, un script Python, un webhook, ou toute intégration externe.",
    tips:
      "Nomme chaque clé selon son usage (claude-agent, script-daily, qa-bot). Si une clé est compromise, révoque-la immédiatement sans toucher aux autres. Les clés ont les permissions read + admin par défaut.",
    example:
      "Agent Claude qui récupère ton profil : GET /perform-api?action=profile avec le header Bearer → retourne ton sport_level, tes objectifs et tes séances récentes.",
  },
  "/connect-claude": {
    title: "Connecter Claude (Desktop / Code)",
    category: "advanced",
    icon: "🤖",
    order: 1,
    purpose: "Claude Desktop peut se connecter directement à PERFORM via le protocole MCP (Model Context Protocol). Une fois configuré, Claude lit ton profil, tes séances et ton planning sans que tu aies à tout recopier.",
    howTo: `Depuis Claude Desktop :
1. Menu ≡ → Paramètres → Développeur → Modifier la config
2. Colle le bloc JSON ci-dessous dans la section "mcpServers"
3. Redémarre Claude Desktop
4. Tape 'performe' dans le chat — Claude se connecte automatiquement.

Config à coller :
{
  "mcpServers": {
    "perform": {
      "command": "npx",
      "args": ["-y", "@perform/mcp-server"],
      "env": { "PERFORM_API_KEY": "ta-clé-api" }
    }
  }
}

Ta clé API : Profil → Paramètres → Clé API PERFORM.`,
    whenToUse: "Configuration unique. Fonctionne ensuite automatiquement à chaque ouverture de Claude Desktop.",
    tips: "Si Claude dit qu'il ne trouve pas le serveur MCP, vérifie que Node.js est installé (node -v dans le terminal). La clé API est personnelle — ne la partage pas.",
    example: "Après setup, tu ouvres Claude Desktop et tu tapes 'performe' → Claude répond avec ton dashboard : profil, séance du jour, prochains entraînements.",
  },
  "/connect-chatgpt": {
    title: "Connecter ChatGPT (Web + Desktop)",
    category: "advanced",
    icon: "💬",
    order: 2,
    purpose: "ChatGPT peut accéder à ton profil PERFORM via les Instructions personnalisées (Custom Instructions). C'est une configuration manuelle mais simple — tu colles un prompt une seule fois.",
    howTo: `Sur ChatGPT Web (chatgpt.com) :
1. Clique sur ton avatar → Paramètres → Personnalisation → Instructions personnalisées
2. Dans le champ "Ce que ChatGPT doit savoir sur vous", colle le prompt PERFORM ci-dessous
3. Sauvegarde — la config se synchronise avec l'app desktop automatiquement.

Note : ChatGPT Desktop sync avec la version Web — configure sur le Web, ça marche partout.
Pour activer les instructions personnalisées : Paramètres → Fonctionnalités bêta → Instructions personnalisées = ON.`,
    whenToUse: "Configuration unique. ChatGPT utilisera ton contexte PERFORM dans toutes les nouvelles conversations.",
    tips: "Les instructions personnalisées ont une limite de caractères (~1500). Utilise le prompt compact fourni. Si tu veux plus de détails, mets à jour manuellement après chaque mise à jour de programme.",
    example: "Après setup, tu ouvres ChatGPT et tu tapes 'performe' → il répond avec ton dashboard basé sur les infos de ton profil.",
  },
  "/connect-prompts": {
    title: "Prompts prêts à copier",
    category: "advanced",
    icon: "📋",
    order: 3,
    purpose: "Prompts optimisés par IA pour connecter PERFORM à Claude ou ChatGPT. Colle-les une fois, l'IA comprend tout ton contexte sportif automatiquement.",
    howTo: `PROMPT CLAUDE (à coller dans Claude.ai → Projects → Instructions du projet) :

---
Tu es connecté à PERFORM Athletic, ma plateforme de suivi sportif personnalisée.
Quand je tape 'performe', donne-moi un dashboard : état profil, séance du jour, prochains entraînements, 1 conseil.
Mes données sont accessibles via le serveur MCP PERFORM.
Sois direct, court, sans intro. Réponds en français.
---

PROMPT CHATGPT (à coller dans Instructions personnalisées) :

---
J'utilise PERFORM Athletic pour suivre mon entraînement sportif.
Contexte : [colle ici ton résumé profil depuis PERFORM → Profil → Exporter le résumé]
Quand je dis 'performe', fais un bilan rapide : profil, séance du jour, agenda semaine, 1 conseil prioritaire.
Réponse courte, en français, format liste.
---`,
    whenToUse: "À la première configuration de ton IA. Mets à jour le prompt ChatGPT quand ton programme change.",
    tips: "Pour Claude : le MCP envoie les données en temps réel. Pour ChatGPT : colle manuellement ton résumé profil (disponible dans Profil → Exporter).",
    example: "'performe' → dashboard instantané, peu importe l'IA utilisée.",
  },
  "/brain-weekly": {
    title: "Bilan semaine avec le Cerveau",
    category: "advanced",
    icon: "📊",
    order: 4,
    purpose: "Parle de ta semaine au Cerveau en langage naturel — il adapte ton programme automatiquement.",
    howTo: "Dans le chat, décris ta semaine passée et/ou tes plans. Exemples : 'j'ai raté 2 séances cette semaine, j'ai mal au genou' ou 'la semaine pro je prévois 4 entraînements'. Le Cerveau analyse et suggère des ajustements.",
    whenToUse: "Chaque dimanche soir pour préparer la semaine suivante, ou quand ta semaine ne s'est pas passée comme prévu.",
    tips: "Plus tu es précis (séances faites, nutrition, sommeil, douleurs), plus les adaptations seront pertinentes. Pas besoin de format particulier — parle normalement.",
    example: "'J'ai fait 2 séances sur 4 cette semaine, j'ai mangé peu de protéines et j'ai mal aux genoux depuis jeudi' → le Cerveau : bilan, ajustements charge/nutrition, action prioritaire.",
  },
};

/** Get help content for a given route, with fallback */
export function getHelpForRoute(pathname: string): HelpEntry | null {
  // Exact match first
  if (helpContent[pathname]) return helpContent[pathname];
  // Prefix match (e.g. /programs/123 → /programs)
  const base = "/" + pathname.split("/").filter(Boolean)[0];
  return helpContent[base] || null;
}

/** Get all guide entries (entries with a category), sorted and optionally filtered by mode */
export function getGuideEntries(currentMode?: "simple" | "core" | "advanced"): { key: string; entry: HelpEntry }[] {
  const modeOrder: Record<string, number> = { simple: 0, core: 1, advanced: 2 };
  const currentLevel = currentMode ? modeOrder[currentMode] ?? 2 : 2;

  return Object.entries(helpContent)
    .filter(([, entry]) => entry.category)
    .filter(([, entry]) => {
      if (!entry.minMode) return true; // No minMode = always visible
      return modeOrder[entry.minMode] <= currentLevel;
    })
    .map(([key, entry]) => ({ key, entry }))
    .sort((a, b) => (a.entry.order ?? 99) - (b.entry.order ?? 99));
}

/** Get preview entries: entries from higher modes that the user can't yet access */
export function getGuidePreviewEntries(currentMode: "simple" | "core" | "advanced"): { key: string; entry: HelpEntry }[] {
  const modeOrder: Record<string, number> = { simple: 0, core: 1, advanced: 2 };
  const currentLevel = modeOrder[currentMode] ?? 0;
  // Show entries from the NEXT mode up only
  const nextLevel = currentLevel + 1;

  return Object.entries(helpContent)
    .filter(([, entry]) => entry.category && entry.minMode && entry.previewText)
    .filter(([, entry]) => modeOrder[entry.minMode!] === nextLevel)
    .map(([key, entry]) => ({ key, entry }))
    .sort((a, b) => (a.entry.order ?? 99) - (b.entry.order ?? 99));
}
